<template>
  <Teleport to="body">
    <Transition name="confirm-dialog-fade">
      <div
        v-if="modelValue"
        class="confirm-dialog-overlay"
        role="presentation"
        @click="handleOverlayClick"
      >
        <section
          class="confirm-dialog"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          :aria-describedby="messageId"
          @click.stop
        >
          <header class="confirm-dialog__header">
            <h2 :id="titleId" class="confirm-dialog__title">{{ title }}</h2>
            <button
              class="confirm-dialog__close"
              type="button"
              :aria-label="closeLabel"
              @click="handleCancel"
            >
              <SvgIcon name="modal-close" :size="11" />
            </button>
          </header>

          <div :id="messageId" class="confirm-dialog__message">
            <slot>{{ message }}</slot>
          </div>

          <footer class="confirm-dialog__footer">
            <button
              class="confirm-dialog__button confirm-dialog__button--cancel"
              type="button"
              @click="handleCancel"
            >
              {{ cancelText }}
            </button>
            <button
              class="confirm-dialog__button confirm-dialog__button--confirm"
              type="button"
              @click="handleConfirm"
            >
              {{ confirmText }}
            </button>
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';

  interface Props {
    modelValue: boolean;
    title?: string;
    message?: string;
    cancelText?: string;
    confirmText?: string;
    closeLabel?: string;
    closeOnOverlay?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    title: '提示',
    message: '',
    cancelText: '取消',
    confirmText: '确定',
    closeLabel: '关闭弹窗',
    closeOnOverlay: true
  });

  const emit = defineEmits<{
    'update:modelValue': [value: boolean];
    cancel: [];
    confirm: [];
  }>();

  const dialogId = Math.random().toString(36).slice(2);
  const titleId = computed(() => `confirm-dialog-title-${dialogId}`);
  const messageId = computed(() => `confirm-dialog-message-${dialogId}`);

  const closeDialog = () => {
    emit('update:modelValue', false);
  };

  const handleCancel = () => {
    emit('cancel');
    closeDialog();
  };

  const handleConfirm = () => {
    emit('confirm');
    closeDialog();
  };

  const handleOverlayClick = () => {
    if (!props.closeOnOverlay) return;

    handleCancel();
  };
</script>

<style scoped>
  .confirm-dialog-overlay {
    position: fixed;
    inset: 0;
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    box-sizing: border-box;
    background: rgba(0, 0, 0, 0.45);
  }

  .confirm-dialog {
    width: 380px;
    max-width: 100%;
    padding: 20px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-radius: 8px;
    background: #fff;
  }

  .confirm-dialog__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 26px;
  }

  .confirm-dialog__title {
    margin: 0;
    color: #0f2f3e;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 23px;
  }

  .confirm-dialog__close {
    width: 26px;
    height: 26px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #0f2f3e;
    cursor: pointer;
  }

  .confirm-dialog__close:hover {
    background: #f3f4f6;
  }

  .confirm-dialog__message {
    min-height: 40px;
    padding: 10px 20px;
    box-sizing: border-box;
    color: #616569;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }

  .confirm-dialog__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
  }

  .confirm-dialog__button {
    width: 76px;
    height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 24px;
    box-sizing: border-box;
    border-radius: 6px;
    font-family: 'Microsoft YaHei', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    white-space: nowrap;
    cursor: pointer;
    transition:
      background 0.15s,
      border-color 0.15s,
      color 0.15s;
  }

  .confirm-dialog__button--cancel {
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #333;
  }

  .confirm-dialog__button--cancel:hover {
    border-color: #d1d5db;
    background: #f9fafb;
  }

  .confirm-dialog__button--confirm {
    border: 0;
    background: #6033e1;
    color: #fff;
  }

  .confirm-dialog__button--confirm:hover {
    background: #5128c7;
  }

  .confirm-dialog__button:focus-visible,
  .confirm-dialog__close:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .confirm-dialog-fade-enter-active,
  .confirm-dialog-fade-leave-active {
    transition: opacity 0.15s ease;
  }

  .confirm-dialog-fade-enter-from,
  .confirm-dialog-fade-leave-to {
    opacity: 0;
  }

  @media (max-width: 767px) {
    .confirm-dialog-overlay {
      padding: 16px;
    }

    .confirm-dialog {
      width: 100%;
    }
  }
</style>
